import React, { useCallback, useEffect, useState } from 'react';
import LateralPanel from '../../../../../muiComponents/LateralPanel';
import IconButton from '../../../../../muiComponents/IconButton';
import Title from '../../../../components/Title';
import {
    ConnectionRequestState,
    MemberState,
    ZinantisMemberHotelsState,
    ZinantisRelationshipStatusTypeKeys,
} from '../../../../../types/zinantis/members/membersType';
import Image from '../../../../components/Image';
import Grid from '../../../../../muiComponents/Grid';
import GridChild from '../../../../../muiComponents/GridChild';
import Flag from '../../../components/Flag';
import api from '../../../../../services/api';
import { viewModelEntities } from '../../../../../utils/commonVariables/entityKeys';
import endpoints from '../../../../../utils/commonVariables/endpoints';
import { viewModelZinantisMemberDetail } from '../../../../../utils/viewModelStructures/zinantisMemberDetail';
import { IAgencyDescription } from '../../../../../types/entities/IAgencyDescription';
import { adaptMemberDescriptionToState } from '../../../../../services/zinantis/members/zinantisMemberListAdapters';
import { useNotification } from '../../../../../contexts/NotificationsContext';
import {
    formatDate,
    getPastDate,
    isMinValueDate,
} from '../../../../../utils/formatDates';
import Tag from '../../../../../muiComponents/Tag';
import LoadingPlaceholder from '../../../../../muiComponents/LoadingPlaceholder';
import { memberDisconnectedStatusColor } from '../../../../../utils/zinantis/members/membersUtils';
import { useHotelOwnerName } from '../../../../../contexts/HotelOwnerContext';
import ConnectionRequestSection from './ConnectionRequestSection';
import HotelsSection from './HotelsSection';
import DescriptionSection from './DescriptionSection';
import { getRandomNumber } from '../../../../../utils/usefulFunctions';
import { FeedbackMessages } from '../../../../../utils/commonVariables/messages';
import useTranslate from '../../../../../customHooks/useTranslate';
import useLanguage from '../../../../../customHooks/useLanguage';
import {
    doubleMD_ColumnGrid,
    full_ColumnGrid,
    gridSpacing20,
    gridSpacing30,
} from '../../../../../utils/commonVariables/grid';

interface MemberPanelProps {
    open: boolean;
    onClose: () => void;
    memberId: string;
    status: string;
    statusKey: ZinantisRelationshipStatusTypeKeys;
    statusColor: string;
    memberHotels: ZinantisMemberHotelsState[];
    allHotels: boolean;
    connectionRequests: ConnectionRequestState[];
}

const MemberPanel = (props: MemberPanelProps) => {
    const {
        open,
        onClose,
        memberId,
        statusKey = ZinantisRelationshipStatusTypeKeys.DISCONNECTED,
        status = 'disconnected',
        statusColor = memberDisconnectedStatusColor,
        memberHotels = [],
        allHotels = false,
        connectionRequests = [],
    } = props;
    const translate = useTranslate();
    const language = useLanguage();
    const { setErrorMessage } = useNotification();
    const ownerName = useHotelOwnerName();
    const [member, setMember] = useState<Partial<MemberState> | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        getMember();
    }, []);

    const getMember = useCallback(async () => {
        try {
            setLoading(true);
            const endpoint = endpoints.viewModel(
                viewModelEntities.Agency,
                memberId
            );
            const {
                data: { Result: memberData },
            } = await api.post(endpoint, viewModelZinantisMemberDetail);

            const twoMonthsAgo = getPastDate(2, 'month');

            const isNew = memberData.ZinantisMemberDate
                ? new Date(memberData.ZinantisMemberDate) >=
                  new Date(twoMonthsAgo)
                : false;

            setMember({
                id: memberData.Id,
                name: memberData.Name || '',
                address: memberData.Address || '',
                country:
                    memberData.Country?.LocalizedCaption ||
                    memberData.Country?.Caption ||
                    memberData.Country?.Name ||
                    '',
                countryCode: memberData.Country?.ISO2 || '',
                idCountry: memberData.IdCountry || 0,
                logo: memberData.LogoFile?.VirtualPath || '',
                idAccountManagementSegment:
                    memberData.IdAccountManagementSegment || 0,
                accountManagementSegment:
                    memberData.AccountManagementSegment?.LocalizedCaption ||
                    memberData.AccountManagementSegment?.Caption ||
                    memberData.AccountManagementSegment?.Name ||
                    '',
                descriptions: memberData.Descriptions.map(
                    (descr: IAgencyDescription) =>
                        adaptMemberDescriptionToState(descr)
                ),
                website: memberData.WebSite || '',
                isNew,
                bookingCodePrefix: memberData.BookingCodePrefix || '',
                signedDate: memberData.ZinantisMemberDate || null,
            } as MemberState);
        } catch (error) {
            setErrorMessage(FeedbackMessages.ERROR_FETCHING);
        } finally {
            setLoading(false);
        }
    }, [memberId]);

    const formatExternalUrl = (url: string) => {
        if (!url) return '#';
        if (!/^https?:\/\//i.test(url)) {
            return `https://${url}`;
        }
        return url;
    };

    const handleLoading = (value: boolean) => {
        setLoading(value);
    };

    return (
        <LateralPanel
            open={open}
            onClose={onClose}
            className='member-panel padding40'
        >
            <div className='flex width100perc flex-end'>
                <IconButton iconName='Close' onClick={onClose} />
            </div>
            {loading ? (
                <div className='flex flex-column gap-20 width100perc height100perc min-height-0'>
                    {Array.from({ length: 10 }).map((_, i) => (
                        <LoadingPlaceholder
                            key={i}
                            className='loading-item'
                            variant='rounded'
                            sx={{ flexGrow: getRandomNumber(0, 5) }}
                        />
                    ))}
                </div>
            ) : (
                <section className='flex-column gap-20'>
                    {member?.logo ? (
                        <div
                            style={{
                                aspectRatio: 3 / 2,
                                width: '100%',
                                backgroundColor: 'var(--always-white)',
                                overflow: 'hidden',
                                height: 'fit-content',
                                borderRadius: '8px',
                                border: '1px solid var(--border)',
                            }}
                        >
                            {member?.logo ? (
                                <Image
                                    style={{
                                        display: 'block',
                                        maxWidth: '100%',
                                        width: '100%',
                                    }}
                                    src={member.logo}
                                    alt={member?.name}
                                />
                            ) : (
                                ''
                            )}
                        </div>
                    ) : (
                        ''
                    )}
                    <div className='flex-column gap-20'>
                        <div className='flex gap-20 flex-between flex-align-baseline '>
                            <h1 className='lateral-panel__title '>
                                {member?.name}
                            </h1>
                            {member?.isNew && <Tag text='new' color='pink' />}
                        </div>

                        <ConnectionRequestSection
                            onChangeLoading={handleLoading}
                            status={status}
                            statusColor={statusColor}
                            statusKey={statusKey}
                            idAgency={memberId}
                            connectionRequests={connectionRequests}
                            agencyName={member?.name || ''}
                            ownerName={ownerName || ''}
                        />
                        <div className='flex-column gap-20'>
                            <Grid
                                columnSpacing={gridSpacing30}
                                rowSpacing={gridSpacing20}
                                style={{ width: '100%' }}
                            >
                                {member &&
                                member.descriptions &&
                                member.descriptions.length ? (
                                    <GridChild gridProps={full_ColumnGrid}>
                                        <DescriptionSection
                                            descriptions={
                                                member?.descriptions || []
                                            }
                                        />
                                    </GridChild>
                                ) : null}
                                {member?.address ? (
                                    <GridChild gridProps={doubleMD_ColumnGrid}>
                                        <>
                                            <Title>
                                                {translate('address')}
                                            </Title>
                                            <p>{member?.address || ''}</p>
                                        </>
                                    </GridChild>
                                ) : (
                                    ''
                                )}

                                {member?.countryCode && member?.country ? (
                                    <GridChild gridProps={doubleMD_ColumnGrid}>
                                        <>
                                            <Title>
                                                {translate('country')}
                                            </Title>
                                            <div className='flex flex-align-center gap-5'>
                                                <Flag
                                                    text={member.country}
                                                    code={member.countryCode}
                                                />
                                                <p>{member.country}</p>
                                            </div>
                                        </>
                                    </GridChild>
                                ) : (
                                    ''
                                )}
                                {member?.idAccountManagementSegment &&
                                member?.accountManagementSegment ? (
                                    <GridChild gridProps={doubleMD_ColumnGrid}>
                                        <>
                                            <Title>
                                                {translate('segment')}
                                            </Title>
                                            <p>
                                                {
                                                    member?.accountManagementSegment
                                                }
                                            </p>
                                        </>
                                    </GridChild>
                                ) : (
                                    ''
                                )}
                                {member?.website ? (
                                    <GridChild gridProps={doubleMD_ColumnGrid}>
                                        <>
                                            <Title>
                                                {translate('website')}
                                            </Title>
                                            <a
                                                className='member-panel__website'
                                                href={formatExternalUrl(
                                                    member.website
                                                )}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                            >
                                                {member.website}
                                            </a>
                                        </>
                                    </GridChild>
                                ) : (
                                    ''
                                )}
                                {member?.bookingCodePrefix ? (
                                    <GridChild gridProps={doubleMD_ColumnGrid}>
                                        <>
                                            <Title>
                                                {translate(
                                                    'booking_code_prefix'
                                                )}
                                            </Title>
                                            <p>{member?.bookingCodePrefix}</p>
                                        </>
                                    </GridChild>
                                ) : (
                                    ''
                                )}
                                {member?.signedDate &&
                                !isMinValueDate(member?.signedDate) ? (
                                    <GridChild gridProps={doubleMD_ColumnGrid}>
                                        <>
                                            <Title>
                                                {translate('member_since')}
                                            </Title>
                                            <p>
                                                {formatDate(
                                                    member?.signedDate,
                                                    language
                                                )}
                                            </p>
                                        </>
                                    </GridChild>
                                ) : (
                                    ''
                                )}
                            </Grid>
                        </div>
                        {statusKey ===
                            ZinantisRelationshipStatusTypeKeys.CONFIRMED &&
                        ((memberHotels && memberHotels.length) || allHotels) ? (
                            <HotelsSection
                                hotels={memberHotels}
                                allHotels={allHotels}
                            />
                        ) : (
                            ''
                        )}
                    </div>
                </section>
            )}
        </LateralPanel>
    );
};

export default MemberPanel;
